import { useAppAccessPermissions } from 'global/cnr/contexts/useHelpers/useAppAccessPermissions';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import React, { Suspense, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Ref, Sidebar } from 'semantic-ui-react';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import PageComponentProvider from '../cnr/contexts/PageComponentContext';
import PageDimmerProvider from '../cnr/contexts/PageDimmerContext';
import { ParentContext } from '../cnr/contexts/ParentContext';
import SplashContextProvider from '../cnr/contexts/SplashContext';
import { startTypes } from '../cnr/reducers/StartReducer';
import { uniqueKey } from '../common/keys';
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import IconGrids from '../components/layout/IconGrids';
import Banner from '../components/sem/SemBanner';
import Banners from '../components/sem/SemBanners';
import AppSidebar from '../components/sidebars/AppSidebar';
import DataSidebar from '../components/swipers/DataSidebar';
import { allPageTypes } from "../enums/allPageTypes";
import { gEnums } from '../enums/globalEnums';
import { _animateTypes } from '../motions/AnimateComponent';
import MotionWait from '../motions/MotionWait';
import { DemoEvent } from '../viewSettings/events/DemoEvent';
import AppWrapper from '../wrappers/AppWrapper';
import DesktopWrapper from '../wrappers/DesktopWrapper';
import Wrapper from "../wrappers/Wrapper";
import AppContentAlt from './AppContentAlt';
import PageAreaContent from './PageAreaContent';
import PageNavigation from './PageNavigation';
import PageTopHeader from './PageTopHeader';

export const AppContentContext = createContext();

// const AppAccessPage = React.lazy(() => import("../components/alerts/AppAccessPage"));
// const RestrictedPage = React.lazy(() => import("../components/alerts/RestrictedPage"));
const QrCode = React.lazy(() => import("../pageItem/modification/pageItemActions/QrCode"));
// const UnderMaintenancePage = React.lazy(() => import("../components/alerts/UnderMaintenancePage"));

const _showMemos = false
/**
 * This is the Main App Content
 * @returns 
 */
const AppContent = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, settings, navigate } = parentContext ?? {}
  const { currentSettings_state, appSettings_state, appUser_state, page_state, paps_state, transition_state } = states
  const { appUser_fns } = fns

  const { aps_global, aps_viewItems, aps_styles, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});

  const { clientSettings } = settings ?? {}
  const { global: global_client } = clientSettings ?? {}
  const { appSignIn: appSignIn_client } = global_client ?? {}
  const { redirectToCurrentEvent } = appSignIn_client ?? {}

  const { transitions } = transition_state ?? {}
  const transition_page = transitions ? transitions[_animateTypes.page] : null
  const { showTransition: showTransition_page } = transition_page ?? {}

  // frameworkContext - not in parent
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const {
    bottomSidebarContent,
    appBottomModalContent,
    cnp,
    desktopMode,
    isMobileDevice,
    isStandAlone,
    permissionGroups,
    showAppBottomModal,
    showAppBottomSidebar,
    showAppQrCode,
    showSignIn,
    useShortLink,
  } = framework_state ?? {}

  // authContext 
  const { appUser, homePageResponded } = appUser_state ?? {}
  const appUserAccess = useAppUserAccess()
  const { loggedIn: loggedIn_au, accessLevel } = appUserAccess ?? {}

  // appSettingsContext 
  const { themeSelected } = appSettings_state ?? {}
  let { showHomeScreenPrompt, showHomeScreenHelp } = appSettings_state ?? {}

  // papsContext 
  const { otherView, landingView, view, pathViews, rootPaths, isLandingPage } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}


  const { topMenu, navigation, appStatus, banners: app_banners, appNotifications, appInstallation, projectOptions, appSignIn } = aps_global ?? {}
  // const { appAccessType, passcode } = appAccess ?? {}
  const { showBannersPages, bannersLocation } = app_banners ?? {}
  const { forceInstall } = appSignIn ?? {}

  const _forceInstall = forceInstall && !isStandAlone && isMobileDevice

  const { useAppDarkMode, allowNewVersionUpdate } = projectOptions ?? {}
  const { menuLocation } = topMenu ?? {}
  const { showNavigation } = navigation ?? {}
  const { eventStatusType } = appStatus ?? {}

  const styleAndClass = aps_styles?.[gEnums?.projectStyles?.pageItemDimmer] ?? { style: {} };

  let allowBannersOnPage = otherView ? false : true

  const isLanding = view === landingView

  // const _view = fromDesktop ? selectedPage : view

  // local state
  const [pageAccess, setPageAccess] = useState({ pending: true })
  const [allowAppView, setAllowAppView] = useState()
  const [showAppAccess, setShowAppAccess] = useState(false)

  const _accessPrms = useAppAccessPermissions()

  const _pageAccess = useMemo(() => {

    if (!aps_page || !aps_global) return null;

    const { allow: _allowPageView } = _accessPrms.validateAccess_pageView();

    const _allowed = _allowPageView === gEnums.viewPermissionTypes.allow;

    return {
      pageAccess: { allowed: _allowed, allowPageView: _allowPageView, pending: false },
      allowAppView: appStatus ? appUser_fns.validateAccess_app(appUser, appStatus) : true
    };

  }, [aps_page, appUserAccess]);

  useEffect(() => {
    setPageAccess(_pageAccess?.pageAccess);
    setAllowAppView(_pageAccess?.allowAppView);
  }, [_pageAccess]);

  useEffect(() => {
    // redirect the appUser to the current event
    if (loggedIn_au && redirectToCurrentEvent && !pathViews.events && appUserAccess.activeEvents_appUser && accessLevel < gEnums.authLevels.appDataAdmin.value) {
      if (appUserAccess.activeEvents_appUser.length > 0) {
        const event_appUser = appUserAccess.activeEvents_appUser[0]
        const _cp = rootPaths.clients + 'events/' + event_appUser + '/landing'
        navigate(_cp)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToCurrentEvent]);

  const contextRef = useRef()

  const banner = useMemo(() => {
    return isLanding || desktopMode ? <Banner /> : null; Fv
  }, [isLanding, desktopMode]);

  const banners = useMemo(() => {
    const allow = showBannersPages?.includes(view);
    return isLanding || desktopMode || allow ? <Banners /> : null;
  }, [isLanding, view]);

  /**
   * 
   * @returns PageAreaContent wrapped in PageComponentProvider
   */
  const pageAreaContent = () => <PageComponentProvider>
    <PageAreaContent />
  </PageComponentProvider>

  const appContent_state = {
    allowNewVersionUpdate,
    appInstallation,
    appNotifications,
    appSignIn,
    bannersLocation,
    css: { container: themeSelected ? 'theme-on theme-on-' + themeSelected : null, menu: menuLocation },
    desktopMode,
    eventKey,
    forceInstall: _forceInstall,
    homePageResponded,
    showHomeScreenHelp,
    showHomeScreenPrompt: showHomeScreenPrompt && !homePageResponded,
    useAppDarkMode,
  }

  const topNav = useMemo(() => {
    return <IconGrids key={uniqueKey('pcpic', 'icn', 'zz')} />
  }, [permissionGroups])

  const desktopPage = () =>
    <Ref innerRef={contextRef}>
      <DesktopWrapper
        topNav={topNav}
        banner={banner}
        banners={banners}
        menu={menuLocation === gEnums.menuLocations.top && <PageTopHeader />}
        content={pageAreaContent()}
        navigation={showNavigation && !otherView && <PageNavigation />}
        // footer={desktopMode && < DesktopFooter />}
        innerRef={contextRef}
      ></DesktopWrapper>
    </Ref>

  const appPage = () => <AppWrapper
    banner={allowBannersOnPage && banner}
    banners={allowBannersOnPage && banners}
    content={pageAreaContent()}
    menu={menuLocation === gEnums.menuLocations.top && <PageTopHeader />}
    navigation={showNavigation && !desktopMode && !otherView && <PageNavigation />}
  ></AppWrapper>

  const otherContent = () => {

    let oc;

    switch (otherView) {
      case allPageTypes.appProfile:
      case allPageTypes.appUserInfo:
      case allPageTypes.help:
      case allPageTypes.signIn:
        let cn;
        if (cnp) { cn += ' ' + cnp }
        oc = <Wrapper
          content={<PageAreaContent />}
          css={{ container: cn, header: 'padd5' }}
        />
        break;
      default:
        oc = <PageAreaContent />
    }

    // if (desktopMode) {
    //   return <Ref innerRef={contextRef}>
    //     <Desktop
    //       headerContent={<PageTopHeader />}
    //       appContent={oc}
    //     />
    //   </Ref>
    // } else {
    return appPage()
    // }
  }

  const appMainContent = () => {
    if (otherView) {
      return otherContent()
    } else {
      return desktopMode ? desktopPage() : appPage()
    }
  }

  const mainContent = () => <AppSidebar>
    {appMainContent()}
  </AppSidebar>

  const appContentOther = () => <Suspense fallback={<SuspenseDimmer origin={'App Content'} />}>
    <QrCode qrCodeType={gEnums.qrCodeTypes.appLink} fromSidebar={true} useShortLink={useShortLink} />
  </Suspense>

  /**
   * 
   * @returns app content
   */
  const appContent = () => {
    if (showAppQrCode) {
      return appContentOther()
    } else if ((showSignIn && !loggedIn_au)) {
      // show the sign in page
      return <AppContentAlt view={view} pageAccess={pageAccess} appUser={appUser} loggedIn={loggedIn_au} fromDirect={true} />
    } else if (pageAccess?.allowed) {
      return mainContent()
    } else {
      return <AppContentAlt view={view} pageAccess={pageAccess} appUser={appUser} loggedIn={loggedIn_au} />
    }
  }

  const sidebar_appBottom = (visible) => <DataSidebar direction={'bottom'} visible={visible} content={visible && bottomSidebarContent} onHide={framework_handlers.handleShow_appBottomSidebar} />
  const sidebar_appBottomModal = (visible) => <DataSidebar direction={'bottom'} visible={visible} content={visible && appBottomModalContent} onHide={framework_handlers.handleHide_appBottomModal} />

  /**
   * 
   * @returns Sidebar
   * this is inside a useMemo
   */
  const app_withSidebar = () => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_appBottomModal(showAppBottomModal)}
    {sidebar_appBottom(showAppBottomSidebar)}
    <Sidebar.Pusher dimmed={showAppBottomSidebar || showAppBottomModal} className='h100' >
      {appContent()}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const pageContent = () => {

    _showMemos && console.log('useMemo > AppContent', view, pathViews)

    switch (view) {
      case 'demo':
        return <DemoEvent />;
      default:
        // if (showAppAccess) {

        //   return <AppAccessPage />;
        // } else {
        switch (allowAppView) {
          // case gEnums.eventStatusTypes.restricted:
          //   return <RestrictedPage />;
          default:
            switch (eventStatusType) {
              // case gEnums.eventStatusTypes.underMaintenance:
              //   return <UnderMaintenancePage />;
              default:
                if (pageAccess?.pending) {
                  return (
                    <MotionWait inverted={true} styleAndClass={styleAndClass} startType={startTypes.pageItem} origin={'AppContent'} />
                  );
                } else {
                  return (
                    <AppContentContext.Provider value={{ appContent_state: { ...appContent_state } }}>
                      {app_withSidebar()}
                    </AppContentContext.Provider>
                  );
                }
            }
        }
      // }
    }
  }


  const appPageContent = () => {
    return showTransition_page ? (
      <PageDimmerProvider>{pageContent()}</PageDimmerProvider>
    ) : (
      pageContent()
    );
  }

  return <SplashContextProvider>
    {appPageContent()}
  </SplashContextProvider>

}

export default AppContent